import { LimitType, ServiceProductStatus } from "model/schema/globalTypes";
import { ApiKeyWithServiceProducts_publicApi_apiKeys_apiKey_attachedServiceProducts } from "../../public-api/schema/ApiKeyWithServiceProducts";
import { CreateServiceProduct_solutions_solution_useCases_serviceProducts_create_notificationDeliveryStatuses as NotificationDeliveryStatus } from "../hooks/schema/CreateServiceProduct";

export type { NotificationDeliveryStatus };

export interface ServiceProductCreatable {
  apiKeyId: string;
  name: string;
  limitType: LimitType;
  transactionLimit: number;
  periods: ServiceProductPeriodInput[];
  services: Key[];
  notificationTriggers: ServiceProductNotificationTriggers[];
}

export interface ServiceProductNotificationTriggers {
  email: string;
  thresholds: number[];
}

export interface ServiceProductPeriodInput {
  startDate: Date;
  endDate: Date;
}
export interface ServiceProductPeriod extends ServiceProductPeriodInput {
  usedTransactionCount: number;
}

export interface ServiceProduct extends ServiceProductCreatable {
  readonly id: string;
  readonly apiKey?: ApiKey;
  readonly currentPeriod: ServiceProductPeriod | null;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly periodCount: number;
  readonly currentPeriodNumber: number | null;
  readonly status: ServiceProductStatus;
  readonly notificationTriggers: ServiceProductNotificationTriggers[];
  readonly notificationDeliveryStatuses: NotificationDeliveryStatus[];
  readonly periods: ServiceProductPeriod[];
}

export interface ServiceProductWithSolution
  extends Omit<
    ServiceProductCreatable,
    "notificationTriggers" | "notificationDeliveryStatuses"
  > {
  readonly id: string;
  readonly apiKey?: ApiKey;
  readonly solutionKey: string;
  readonly currentPeriod: ServiceProductPeriod | null;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly periodCount: number;
  readonly currentPeriodNumber: number | null;
  readonly status: ServiceProductStatus;
  readonly periods: ServiceProductPeriod[];
}

export interface ApiKey {
  readonly name: string;
  readonly id: string;
}

export function mapAttachedServiceProductsToServiceProductsWithSolution(
  attachedServiceProducts: ApiKeyWithServiceProducts_publicApi_apiKeys_apiKey_attachedServiceProducts[],
  apiKeyId: string
): ServiceProductWithSolution[] {
  return attachedServiceProducts.map(({ solutionKey, serviceProduct }) => ({
    // Basic fields - direct mapping
    id: serviceProduct.id,
    name: serviceProduct.name,
    transactionLimit: serviceProduct.transactionLimit,
    limitType: serviceProduct.limitType,
    services: serviceProduct.services,

    // Added fields for this interface
    apiKeyId,
    solutionKey,

    // DateTime fields - already Date objects, direct mapping
    startDate: serviceProduct.details.startDate,
    endDate: serviceProduct.details.endDate,

    // Periods - already have Date objects, just map the structure
    periods: serviceProduct.details.allPeriods || [],

    // Current period - already has Date objects, conditional mapping
    currentPeriod: serviceProduct.details.currentPeriod,

    // Other fields - direct mapping
    periodCount: serviceProduct.details.periodCount,
    currentPeriodNumber: serviceProduct.details.currentPeriodNumber ?? 0,
    status: serviceProduct.details.status,
    apiKey: serviceProduct.apiKey
  }));
}

export function hasNotificationProperties(
  obj: ServiceProduct | ServiceProductWithSolution
): obj is ServiceProduct {
  return "notificationTriggers" in obj && "notificationDeliveryStatuses" in obj;
}
